import React, {
    useContext,
    useEffect,
    useMemo,
    useReducer,
    useState,
} from "react";
import { useSelector } from "react-redux";
import { Row, TabsProps, Tabs } from "antd";
import {
    IChannel,
    IDeepDiveModalConfig,
    IMainContext,
    IProductAnalysisModule,
    ProductDeepdiveModules,
    TActiveClientId,
    TCommonRowData,
} from "@ts/common-types";
import { GridApi } from "ag-grid-community";
import {
    fetchDeepDiveModulesData,
    fetchOverviewSectionsData,
} from "@ts/retrieve-data";
import { getSelectedDateRange } from "@ts/date-range";
import { TDate } from "@ts/funnel";
import {
    mainContext,
    PA_MODULE_INITIAL_DATA,
    PA_MODULE_LOADING_INTIAL_STATE,
    productDeepDiveModuleSections,
    SKU_DEPENDENT_MODULES,
} from "@ts/constants";
import { ProductInfoWidget, WidgetLoader } from "@components/widgets";
import ProductAnalysisOverviewModule from "./ProductAnalysisOverviewModule";
import ProductAnalysisRatingsAndReviewsModule from "./ProductAnalysisRatingsAndReviewsModule";
import { loadingReducer } from "@ts/widget";
import { getActiveModule, titleCase } from "@ts/utils";
import { getDataBasedOnFrequency } from "@/ts/widget";
import { includes } from "underscore";
import ProductAnalysisPricingModule from "./ProductAnalysisPricingModule";
import ProductAnalysisVisibilityModule from "./ProductAnalysisVisibilityModule";
import ProductAnalysisAvailabilityModule from "./ProductAnalysisAvailabilityModule";

const ProductAnalysisModule: React.FC<IProductAnalysisModule> = ({
    selectedSKU,
    setSelectedSKU,
    defaultActiveModule,
}) => {
    const { DateRange, dates, setCampaignsLoading } =
        useContext<IMainContext>(mainContext);
    const activeClientId = useSelector(
        (state: TActiveClientId) => state.activeClientId.value
    );
    const currencySymbol = useSelector(
        (state: IChannel) => state.channelCurrencySymbol.value
    );
    const channel = useSelector((state: IChannel) => state.channel.value);
    const [reviewsData, setReviewsData] = useState<any>({});
    const [pricingData, setPricingData] = useState<any>({});
    const [visibilityData, setVisibilityData] = useState<any>({});
    const [availabilityData, setAvailabilityData] = useState<any>({});
    const [loadingState, setLoadingState] = useReducer(
        loadingReducer,
        PA_MODULE_LOADING_INTIAL_STATE
    );
    const [activeTab, setActiveTab] = useState<string>(
        defaultActiveModule || "overview"
    );
    const [moduleData, setModuleData] = useState<IDeepDiveModalConfig>(
        PA_MODULE_INITIAL_DATA
    );
    const [rowData, setRowData] = useState<TCommonRowData | null>(null);
    const [gridApi, setGridApi] = useState<GridApi | null>(null);
    const date: TDate = getSelectedDateRange();
    const { selectedDataFrequency } =
                useContext<IMainContext>(mainContext);

    const resetAll = () => {
        setRowData(null);
        setGridApi(null);
        setSelectedSKU("");
        setModuleData(PA_MODULE_INITIAL_DATA);
        setReviewsData({});
        setLoadingState({
            type: "RESET",
            payload: PA_MODULE_LOADING_INTIAL_STATE,
        });
        setActiveTab(ProductDeepdiveModules.OVERVIEW);
    };

    useEffect(() => {
        if (!selectedSKU) return;

        fetchOverviewSectionsData({
            setLoadingState,
            setSelectedSKU,
            selectedSKU,
            setCampaignsLoading,
            setRowData,
            setModuleData,
            date,
        });

        fetchDeepDiveModulesData(
            ProductDeepdiveModules.RATINGS_REVIEWS,
            selectedSKU,
            date,
            setReviewsData,
            setLoadingState
        );

        fetchDeepDiveModulesData(
            ProductDeepdiveModules.PRICING,
            selectedSKU,
            date,
            setPricingData,
            setLoadingState
        );
        
        fetchDeepDiveModulesData(
            ProductDeepdiveModules.AVAILABILITY,
            selectedSKU,
            date,
            setAvailabilityData,
            setLoadingState
        );

        fetchDeepDiveModulesData(
            ProductDeepdiveModules.VISIBILITY,
            selectedSKU,
            date,
            setVisibilityData,
            setLoadingState
        );
        
        fetchDeepDiveModulesData(
            ProductDeepdiveModules.VISIBILITY,
            selectedSKU,
            date,
            setVisibilityData,
            setLoadingState,
            productDeepDiveModuleSections.KEYWORD_VISIBILITY
        );

        return () => {
            !includes(SKU_DEPENDENT_MODULES,getActiveModule().activeModule) && resetAll();
        };
    }, [selectedSKU, dates, DateRange, channel, activeClientId]);

    const items: TabsProps["items"] = useMemo(() => {
        return [
            {
                key: ProductDeepdiveModules.OVERVIEW,
                label: titleCase(ProductDeepdiveModules.OVERVIEW),
                children: (
                    <ProductAnalysisOverviewModule
                        {...{
                            loadingState,
                            moduleData,
                            rowData,
                            setRowData,
                            gridApi,
                            setGridApi,
                        }}
                    />
                ),
            },
            {
                key: ProductDeepdiveModules.VISIBILITY,
                label: titleCase(ProductDeepdiveModules.VISIBILITY),
                children: <ProductAnalysisVisibilityModule
                {...{
                    loadingState,
                    selectedSKU,
                    visibilityData : getDataBasedOnFrequency(visibilityData, selectedDataFrequency),
                    channel,
                    currencySymbol
                }}
                />,
            },
            {
                key: ProductDeepdiveModules.AVAILABILITY,
                label: titleCase(ProductDeepdiveModules.AVAILABILITY),
                disabled: false,
                children: <ProductAnalysisAvailabilityModule
                {...{
                    loadingState,
                    selectedSKU,
                    availabilityData: getDataBasedOnFrequency(availabilityData, selectedDataFrequency),
                    channel,
                    currencySymbol
                }}
                />,
            },
            {
                key: ProductDeepdiveModules.PRICING,
                label: titleCase(ProductDeepdiveModules.PRICING),
                children: <ProductAnalysisPricingModule
                {...{
                    loadingState,
                    selectedSKU,
                    pricingData: getDataBasedOnFrequency(pricingData, selectedDataFrequency),
                    channel,
                    currencySymbol
                }}
                />,
            },
            {
                key: ProductDeepdiveModules.RATINGS_REVIEWS,
                label: "Ratings and Reviews",
                children: (
                    <ProductAnalysisRatingsAndReviewsModule
                        {...{
                            loadingState,
                            selectedSKU,
                            reviewsData: getDataBasedOnFrequency(reviewsData, selectedDataFrequency),
                            channel
                        }}
                    />
                ),
            },
            // {
            //     key: ProductDeepdiveModules.CONTENT_SCORE,
            //     label: "Content Score",
            //     disabled: true,
            //     children: <div>Sample 7</div>,
            // },
        ];
    }, [moduleData, rowData, gridApi, loadingState, reviewsData, selectedDataFrequency]);

    return loadingState?.[ProductDeepdiveModules.OVERVIEW] ? (
        <WidgetLoader />
    ) : (
        <div className="px-1">
            <Row className="mb-3">
                <ProductInfoWidget widgetData={moduleData} />
            </Row>
            <Row className="w-100">
                <Tabs
                    defaultActiveKey={activeTab}
                    items={items}
                    className="pa-custom-tabs w-100 overflow-hidden"
                    onTabClick={(activeKey) => setActiveTab(activeKey)}
                />
            </Row>
        </div>
    );
};

export default ProductAnalysisModule;
